// A tactile press ripple, attached directly to one element. Self-contained: the
// element wires its own pointer listener and draws its own ripple with inline
// styles — no global init to remember, no marker attribute, no shared stylesheet.
// Just `ripple(Div())` and that element ripples. Chainable.
//
// The ripple grows from the exact press point, sized to reach the element's
// farthest corner so it always fully covers, and holds while pressed — fading
// out on release, so a quick tap and a long press both feel intentional.

const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')
const ms = (full: number) => (reduce?.matches ? 1 : full)

export const ripple = <T extends { el: HTMLElement }>(base: T): T => {
    const host = base.el
    host.style.overflow = 'hidden' // clip the ripple to the element

    let positioned = false

    host.addEventListener('pointerdown', (e: PointerEvent) => {
        // The ripple is absolutely positioned, so the host must be a positioning
        // context. Only claim it if the element isn't already positioned, so we
        // never fight an absolute/fixed layout the component set for itself.
        if (!positioned) {
            if (getComputedStyle(host).position === 'static') host.style.position = 'relative'
            positioned = true
        }

        const rect = host.getBoundingClientRect()
        // Radius reaches whichever corner is farthest from the press point.
        const dx = Math.max(e.clientX - rect.left, rect.right - e.clientX)
        const dy = Math.max(e.clientY - rect.top, rect.bottom - e.clientY)
        const radius = Math.hypot(dx, dy)

        const r = document.createElement('span')
        Object.assign(r.style, {
            position: 'absolute',
            left: e.clientX - rect.left - radius + 'px',
            top: e.clientY - rect.top - radius + 'px',
            width: radius * 2 + 'px',
            height: radius * 2 + 'px',
            borderRadius: '50%',
            background: 'currentColor',
            pointerEvents: 'none',
            opacity: '0',
            transform: 'scale(0)',
            transition: `transform ${ms(400)}ms cubic-bezier(0.2, 0, 0, 1), opacity ${ms(250)}ms linear`,
        })
        host.appendChild(r)
        requestAnimationFrame(() => {
            r.style.transform = 'scale(1)'
            r.style.opacity = '0.18'
        })

        let released = false
        const release = () => {
            if (released) return
            released = true
            r.style.opacity = '0'
            r.addEventListener('transitionend', () => r.remove(), { once: true })
            setTimeout(() => r.remove(), 700) // safety net if transitionend never fires
        }
        // Release can land anywhere (or be cancelled), so listen on the window.
        window.addEventListener('pointerup', release, { once: true })
        window.addEventListener('pointercancel', release, { once: true })
        setTimeout(release, 1500) // never leave a ripple stuck on if no release fires
    })

    return base
}
